import React, { FunctionComponent, useContext, useEffect, useMemo } from "react";
import { Theme, makeStyles, Grid, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { AnswerContext } from "../answer/context/answer-context";
import { FeedbackType, useNutrientsForQuestionLazyQuery } from "../../../api/smartmelts-api/graphql/generated";
import { QuestionSectionHeading } from "../question/question-section-heading";
import { QuestionSectionLoadingIndicator } from "../question/question-section-loading-indicator";
import { NutrientLegend } from "./nutrient-legend";
import { NutrientIndicator } from "./nutrient-indicator/nutrient-indicator";
import { NutritionContext } from "../context/nutrition-context";
import lodash from "lodash";
import { MenuSubcategoryDictionary } from "../../../models/categories";

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    flex: 1,
    width: "100%",
  },
  typo: {
    width: "100%",
  },
  indicatorContainer: {
    width: "100%",
  },
}));

interface INutrientsAnswerProps {}

export const NutrientsAnswer: FunctionComponent<INutrientsAnswerProps> = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { activeAnswer } = useContext(AnswerContext);
  const { isDemo, activeSubcategory } = useContext(NutritionContext);

  const [nutrientsQuery, { loading, data }] = useNutrientsForQuestionLazyQuery();

  useEffect(() => {
    if (activeAnswer) {
      nutrientsQuery({ variables: { questionId: activeAnswer.id, demo: isDemo } });
    }
  }, [nutrientsQuery, activeAnswer, isDemo]);
  const nutrients = useMemo(() => lodash.sortBy(data?.nutrientsForQuestion, (item) => -item.value), [
    data?.nutrientsForQuestion,
  ]);

  const { geneDataGroupTypes } = MenuSubcategoryDictionary[activeSubcategory];

  return (
    <Grid container className={classes.container}>
      <QuestionSectionHeading>
        {t(`twin_communication.answer.${FeedbackType.NUTRIENTS}.heading`)}
      </QuestionSectionHeading>
      <Grid container direction="column" spacing={4}>
        {!loading && data?.nutrientsForQuestion && (
          <>
            <Grid item>
              <Typography align="center" className={classes.typo}>
                {t(`twin_communication.answer.${FeedbackType.NUTRIENTS}.description`)}
              </Typography>
            </Grid>
            <Grid item>
              <NutrientLegend />
            </Grid>
            <Grid item container spacing={4}>
              {nutrients?.map((nutrient) => (
                <>
                  {nutrient && (
                    <Grid item key={nutrient.name} sm={6} className={classes.indicatorContainer}>
                      <NutrientIndicator nutrient={nutrient} geneDataGroupTypes={geneDataGroupTypes} />
                    </Grid>
                  )}
                </>
              ))}
            </Grid>
          </>
        )}
        {loading && <QuestionSectionLoadingIndicator />}
      </Grid>
    </Grid>
  );
};
